import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  MenuItem,
  Select,
  FormHelperText,
  InputLabel,
  Box,
  Typography,
} from '@mui/material';
import CbButton from '../../../../components/Buttons/CbButton';
import TextFieldBase from '../../../../components/inputs/text-fields/base/TextFieldBase';
import { markSubmissionAsLost } from '../../../../api/submissions/submissions.api';
import useNotifications from '../../../../_lib/notifications/useNotifications';

const lostReasons = [
  { label: 'Bound Elsewhere', value: 'BOUND_ELSEWHERE' },
  { label: 'Coverage Not Needed', value: 'COVERAGE_NOT_NEEDED' },
  { label: 'Price', value: 'PRICE' },
  { label: 'Service', value: 'SERVICE' },
  { label: 'Other', value: 'OTHER' },
];

const schema = Yup.object().shape({
  lostReason: Yup.string().required('Lost reason is required'),
  lostReasonDescription: Yup.string().required('Lost note is required'),
});

const MarkAsLostSubmissionModal = ({
  data,
  close,
  title,
}: {
  data: {
    submissionId: string;
    accountId: string;
    submissionNumber: string;
    accountName: string;
  };
  close: () => void;
  title?: string;
}) => {
  const notifications = useNotifications();
  const methods = useForm({
    defaultValues: {
      lostReason: '',
      lostReasonDescription: '',
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (formData: any) => {
    try {
      await markSubmissionAsLost(data.submissionId, {
        data: {
          lostReason: formData.lostReason,
          lostReasonDescription: formData.lostReasonDescription,
        },
      });
      notifications.success('Submission marked as lost.');
      close();
    } catch (err) {
      notifications.error('Unable to mark as lost.');
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={3} mt={2} textAlign="center">
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '1.1rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              mb: 2,
            }}
          >
            Are you sure you want to mark this submission as Lost?
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.35rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              letterSpacing: 0.5,
              textAlign: 'center',
            }}
          >
            {data.submissionNumber}: {data.accountName}
          </Typography>
        </Box>
        <Box style={{ padding: '0 24px' }}>
          <Box mb={2}>
            <InputLabel required id="lost-reason-label">
              Lost Reason
            </InputLabel>
            <Select
              labelId="lost-reason-label"
              fullWidth
              {...register('lostReason')}
              defaultValue=""
              error={!!errors.lostReason}
            >
              {lostReasons.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.lostReason && (
              <FormHelperText error>{errors.lostReason.message}</FormHelperText>
            )}
          </Box>
          <Box mb={2}>
            <TextFieldBase
              label="Lost Note"
              required
              multiline
              minRows={4}
              errorMessage={errors.lostReasonDescription?.message}
              inputProps={{ maxLength: 500 }}
              {...register('lostReasonDescription')}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={2} pb={2}>
            <CbButton
              styleName="cancel"
              onClick={close}
              buttonText="Cancel"
              disabled={isSubmitting}
            />
            <CbButton
              styleName="ctaButton"
              type="submit"
              buttonText="Mark as Lost"
              loading={isSubmitting}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default MarkAsLostSubmissionModal;
